/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * The warning strip for a workspace whose subscription is lapsing or lapsed.
 *
 * Fed by the `subscription` block the backend now folds into
 * /permissions/me and /module-entitlements -- no extra request, and no second
 * source that can disagree with the sidebar's plan badge.
 *
 * During the grace period the tenant still has full access; the banner is a
 * warning, not a restriction. Once grace runs out the hourly lifecycle job
 * suspends the tenant and TenantLocked takes over from here.
 */

import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Collapse } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import IconButton from "@mui/material/IconButton";
import { useTheme } from "@mui/material/styles";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

/** Start warning a month out -- enough notice to get a renewal approved. */
const WARN_WITHIN_DAYS = 30;

function daysUntil(value) {
  if (!value) return null;
  const target = new Date(value);
  if (Number.isNaN(target.getTime())) return null;
  const today = new Date();
  return Math.ceil((target - today) / (1000 * 60 * 60 * 24));
}

function plural(n, word) {
  return `${n} ${word}${Math.abs(n) === 1 ? "" : "s"}`;
}

export default function SubscriptionBanner({ subscription }) {
  const theme = useTheme();
  const [dismissed, setDismissed] = useState(false);

  const notice = useMemo(() => {
    if (!subscription) return null;

    const { subscription_status: status, grace_until: graceUntil, in_grace: inGrace } = subscription;

    // Expired but inside grace: the hard case, and the one worth shouting
    // about, because access disappears at a known date.
    if (inGrace) {
      const left = daysUntil(graceUntil);
      return {
        severity: "error",
        text:
          left !== null && left >= 0
            ? `Your subscription has expired. Access continues for ${plural(left, "day")}, then this workspace will be locked.`
            : "Your subscription has expired and this workspace will be locked shortly.",
      };
    }

    if (status === "PAUSED") {
      return { severity: "warning", text: "Your subscription is paused. Contact your administrator to resume it." };
    }

    // Approaching the end date, still healthy.
    const left = daysUntil(subscription.expires_on);
    if (left !== null && left >= 0 && left <= WARN_WITHIN_DAYS && !subscription.auto_renew) {
      const isTrial = status === "TRIAL";
      return {
        severity: left <= 7 ? "error" : "warning",
        text: `${isTrial ? "Your trial" : "Your subscription"} ends in ${plural(left, "day")}.`,
      };
    }

    return null;
  }, [subscription]);

  if (!notice || dismissed) return null;

  const isError = notice.severity === "error";
  const accent = isError ? theme.palette.error.main : theme.palette.warning.main;

  return (
    <Collapse in>
      <MDBox
        role="status"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: 2,
          py: 1.25,
          mb: 2,
          borderRadius: "12px",
          border: `1px solid ${accent}`,
          backgroundColor: isError
            ? theme.palette.error.background || `${theme.palette.error.main}14`
            : `${theme.palette.warning.main}14`,
        }}
      >
        <WarningAmberIcon sx={{ color: accent, fontSize: 20 }} />
        <MDTypography variant="button" fontWeight="medium" sx={{ flex: 1 }}>
          {notice.text}
        </MDTypography>
        {/* Dismissable for the session only. It returns on the next load,
            because the deadline has not gone away. */}
        <IconButton size="small" aria-label="Dismiss" onClick={() => setDismissed(true)}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </MDBox>
    </Collapse>
  );
}

SubscriptionBanner.propTypes = {
  subscription: PropTypes.shape({
    subscription_status: PropTypes.string,
    expires_on: PropTypes.string,
    grace_until: PropTypes.string,
    in_grace: PropTypes.bool,
    auto_renew: PropTypes.bool,
  }),
};

SubscriptionBanner.defaultProps = { subscription: null };
